import { Card, Typography } from "antd";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  BarChart,
  RadarChart,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  PolarRadiusAxis,
  Rectangle,
  LineChart,
  Line,
} from "recharts";
import { colorsSet } from "../utils";
import noData from "./noData";

interface ChartComponent {
  type: string;
  key: string;
  data: any;
  config: any;
}

const composedChart = ({ type, key, data, config }: ChartComponent) => {
  const { Text } = Typography;
  let dataSplited, top4;
  const isEmpty = data.length ? true : false;
  if (type === "radar" && data.length > 4) {
    dataSplited = checkRadarData(data, key);
    data = dataSplited.data;
    top4 = dataSplited.top;
  }

  const category = data.length
    ? Object.keys(
        data?.reduce((acc: any, item: any) => {
          acc[item[key]] = true;
          return acc;
        }, {}),
      )
    : [];

  if (type === "donut" || type === "pie") {
    data = data?.map((item: any) => ({
      ...item,
      fill: colorsSet[
        category.findIndex((c) => c === item[key]) + config.colorSet
      ],
    }));
  }

  const CustomLegend = (props: any) => {
    const { payload } = props;
    return (
      <div className="customLegend">
        <Card className="custom-legend-container">
          <div
            key={`head-legend-${Math.random()}`}
            style={{ minWidth: config?.legendKey ? "12rem" : "9rem" }}
          >
            <div className="flex justify-between items-center gap-2">
              {config?.legend?.map((head: string) => (
                <div className="custom-text">{head}</div>
              ))}
            </div>
            {payload.map((item: any) => (
              <div
                key={`item-legend-${Math.random()}`}
                className="flex justify-between items-center gap-2 hov"
              >
                <div>
                  <div
                    className="circle"
                    style={{ backgroundColor: item?.payload?.fill }}
                  ></div>
                </div>
                <div className="flex flex-1 gap-2 justify-between">
                  <div className="flex-1 custom-text">
                    {item?.payload?.[key]}:
                  </div>
                  <div className="flex-1">
                    <Text>{item?.payload?.[config.dataKey]}</Text>
                  </div>
                  {config.legendKey && (
                    <div className="flex-2" style={{ minWidth: "4rem" }}>
                      <Text>
                        ${item?.payload?.[config.legendKey].toLocaleString()}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  const CustomTooltip = ({ payload }: any) => {
    return (
      <Card>
        <div className="flex items-center gap-1">
          <div>
            <div
              className="circle"
              style={{ backgroundColor: payload[0]?.payload?.fill }}
            ></div>
          </div>
          <div>
            <Text strong>{payload[0]?.payload?.[key]}:</Text>
          </div>
          <div>
            <Text>{payload[0]?.payload?.[config.dataKey]}</Text>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <>
      {isEmpty ? (
        <>
          {type === "line" ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={data}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={config.xField} />
                <YAxis />
                <Tooltip />
                <Legend verticalAlign="top" />
                {config.yField.map((bit: string, index: number) => (
                  <Line
                    key={`line-colmun-${Math.random()}`}
                    type="monotone"
                    dataKey={bit}
                    stroke={colorsSet[colorsSet.length - index]}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : type === "donut" || type === "pie" ? (
            <PieChart width={500} height={250}>
              <Pie
                data={data}
                dataKey={config.dataKey}
                cx="25%"
                cy="50%"
                paddingAngle={type === "donut" ? 5 : 1}
                innerRadius={type === "donut" ? 75 : 0}
                outerRadius={90}
                label
              />
              {type === "donut" && (
                <text
                  x={"25%"}
                  y={"50%"}
                  dy={5}
                  textAnchor="middle"
                  fill={"#202020"}
                >
                  {config.label}
                </text>
              )}
              <Tooltip content={CustomTooltip} />
              <Legend content={CustomLegend} />
            </PieChart>
          ) : type === "bar" ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart width={500} height={300} data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={config.xField} />
                <YAxis />
                <Tooltip />
                <Legend verticalAlign="top" />
                {config.yField.map((bit: string, index: number) => (
                  <Bar
                    key={`bar-colmun-${Math.random()}`}
                    dataKey={bit}
                    fill={colorsSet[config.colorSet + index]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : type === "radar" ? (
            <>
              <ResponsiveContainer
                width="100%"
                height={top4?.length ? 300 : 350}
              >
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey={config.xField} />
                  <PolarRadiusAxis />
                  <Tooltip />
                  <Radar
                    name="Registrations"
                    dataKey={config.yField}
                    stroke="#3e594f"
                    fill="#BDE2B9"
                    fillOpacity={0.6}
                  />
                </RadarChart>
              </ResponsiveContainer>
              <div>{getTop(top4)}</div>
            </>
          ) : type === "verticalBar" ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                layout="vertical"
                data={data}
                margin={{
                  top: 20,
                  right: 20,
                  bottom: 20,
                  left: 50,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey={config.xField} type="category" scale="auto" />
                <Tooltip />
                <Bar
                  dataKey={config.yField}
                  fill="#F9E0A2"
                  activeBar={<Rectangle fill="#F4B678" stroke="#F4B678" />}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div>No Chart</div>
          )}
        </>
      ) : (
        <>{noData(true)}</>
      )}
    </>
  );
};

const getTop = (top: any) => {
  return (
    <div className="pl-8">
      {top?.length && <div>Top 4 locations</div>}
      <div className="flex justify-center gap-8 items-center">
        {top?.map((t: any) => (
          <div className="flex gap-2 items-center">
            <div className="box"></div>
            <div className="c-text">{t.location}:</div>
            <div className="c-text">{t.total}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const checkRadarData = (data: any, key: string) => {
  data.sort((a: any, b: any) => b[key] - a[key]);
  if (data.length)
    if (data[0][key] - 100 > data[data.length - 1][key]) {
      const top4 = data.slice(0, 4);
      return { data: data.filter((a: any) => !top4.includes(a)), top: top4 };
    } else return { data: data };
  else return { data: data };
};

export default composedChart;
